import { supabase } from './supabase';

// --- Global save status callback ---
let _onSaveError = null;
let _onSaveSuccess = null;
export function setSaveCallbacks(onSuccess, onError) {
  _onSaveSuccess = onSuccess;
  _onSaveError = onError;
}

function handleResult(result, context) {
  if (result.error) {
    console.error('Supabase write error:', context, result.error);
    if (_onSaveError) _onSaveError(`Failed to save ${context}: ${result.error.message}`);
  } else {
    if (_onSaveSuccess) _onSaveSuccess(context);
  }
}

// Guard: reject writes if org_id or user_id would be null (prevents NOT NULL constraint errors)
function requireIds(orgId, userId, context) {
  if (!orgId || !userId) {
    console.error(`${context}: missing orgId (${orgId}) or userId (${userId})`);
    if (_onSaveError) _onSaveError(`Failed to save ${context}: Organization not found — try logging out and back in`);
    return false;
  }
  return true;
}

// --- Sales ---
export function saveSalesEntry(orgId, userId, date, category, amount) {
  if (!requireIds(orgId, userId, 'sales')) return;
  supabase.from('sales').upsert(
    { user_id: userId, org_id: orgId, date, category, amount: parseFloat(String(amount).replace(/,/g,'')) || 0 },
    { onConflict: 'org_id,date,category' }
  ).then(r => handleResult(r, 'sales'));
}

// --- Labor (one row per org per date) ---
// Field-level upsert: only writes the fields passed, never overwrites other fields
export function saveLaborField(orgId, userId, date, fields) {
  if (!requireIds(orgId, userId, 'labor')) return;
  supabase.from('labor').upsert(
    { user_id: userId, org_id: orgId, date, ...fields },
    { onConflict: 'org_id,date' }
  ).then(r => handleResult(r, 'labor'));
}

export function voidDay(orgId, userId, date, reason) {
  if (!requireIds(orgId, userId, 'void day')) return;
  supabase.from('labor').upsert(
    { user_id: userId, org_id: orgId, date, voided: true, void_reason: reason },
    { onConflict: 'org_id,date' }
  ).then(r => handleResult(r, 'void day'));
}
export function unvoidDay(orgId, userId, date) {
  if (!requireIds(orgId, userId, 'unvoid day')) return;
  supabase.from('labor').upsert(
    { user_id: userId, org_id: orgId, date, voided: false, void_reason: null },
    { onConflict: 'org_id,date' }
  ).then(r => handleResult(r, 'unvoid day'));
}

// --- Waste Log ---
export function insertWaste(orgId, userId, w) {
  if (!requireIds(orgId, userId, 'waste')) return;
  supabase.from('waste_log').insert({
    id: w.id, user_id: userId, org_id: orgId, item_id: w.itemId, item_name: w.itemName,
    item_type: w.type, unit: w.unit, qty: w.qty, reason: w.reason, date: w.date, time: w.time
  }).then(r => handleResult(r, 'waste'));
}
export async function insertWasteBatch(orgId, userId, entries) {
  if (!requireIds(orgId, userId, 'waste batch')) return;
  const rows = entries.map(w => ({
    id: w.id, user_id: userId, org_id: orgId, item_id: w.itemId, item_name: w.itemName,
    item_type: w.type, unit: w.unit, qty: w.qty, reason: w.reason, date: w.date, time: w.time
  }));
  const { error } = await supabase.from('waste_log').insert(rows);
  if (error) throw error;
}
export function deleteWaste(id) {
  supabase.from('waste_log').delete().eq('id', id).then(r => handleResult(r, 'delete waste'));
}
export function updateWaste(id, fields) {
  const map = {};
  if ('qty' in fields) map.qty = fields.qty;
  if ('reason' in fields) map.reason = fields.reason;
  if ('date' in fields) map.date = fields.date;
  if (Object.keys(map).length) supabase.from('waste_log').update(map).eq('id', id).then(r => handleResult(r, 'update waste'));
}

// --- Other Expenses (stored in categorized_expenses) ---
export function insertOtherExpense(orgId, userId, e) {
  if (!requireIds(orgId, userId, 'expense')) return;
  supabase.from('categorized_expenses').insert({
    id: e.id, user_id: userId, org_id: orgId, date: e.date, category: e.category || 'Miscellaneous',
    vendor: e.vendor || e.desc, description: e.desc, amount: e.amt
  }).then(r => handleResult(r, 'expense'));
}
export async function updateOtherExpense(id, fields) {
  const payload = {};
  if ('vendor' in fields) payload.vendor = fields.vendor;
  if ('desc' in fields) payload.description = fields.desc;
  if ('amt' in fields) payload.amount = fields.amt;
  if ('date' in fields) payload.date = fields.date;
  if ('category' in fields) payload.category = fields.category;
  if ('amt' in fields) {
    const { data: existing } = await supabase.from('categorized_expenses').select('scanned_snapshot,source_invoice_id').eq('id', id).limit(1).single();
    if (existing?.source_invoice_id && existing?.scanned_snapshot?.amount != null) {
      payload.is_edited = Number(fields.amt) !== Number(existing.scanned_snapshot.amount);
    }
  }
  supabase.from('categorized_expenses').update(payload).eq('id', id).then(r => handleResult(r, 'update expense'));
}
export function deleteOtherExpense(id) {
  supabase.from('categorized_expenses').delete().eq('id', id).then(r => handleResult(r, 'delete expense'));
}

// --- Purchases ---
export function insertPurchase(orgId, userId, p) {
  if (!requireIds(orgId, userId, 'purchase')) return;
  supabase.from('purchases').insert({
    id: p.id, user_id: userId, org_id: orgId, vendor: p.vendor, amount: p.amt, date: p.date, category: p.category || 'Food'
  }).then(r => handleResult(r, 'purchase'));
}
export function updatePurchase(id, fields) {
  const payload = {};
  if ('vendor' in fields) payload.vendor = fields.vendor;
  if ('amt' in fields) payload.amount = fields.amt;
  if ('date' in fields) payload.date = fields.date;
  if ('category' in fields) payload.category = fields.category;
  supabase.from('purchases').update(payload).eq('id', id).then(r => handleResult(r, 'update purchase'));
}
export function deletePurchase(id) {
  supabase.from('purchases').delete().eq('id', id).then(r => handleResult(r, 'delete purchase'));
}

// --- Inventory Counts ---
export function saveInventoryCounts(orgId, userId, date, counts, ingredients) {
  if (!requireIds(orgId, userId, 'inventory')) return;
  const rows = [];
  for (const ig of ingredients) {
    const c = counts[ig.id];
    if (!c) continue;
    rows.push({
      user_id: userId, org_id: orgId, ingredient_id: ig.id, date,
      cases: parseFloat(c.cases) || 0, open_qty: parseFloat(c.open) || 0, open_unit: null,
      count_type: 'full', rotation_week: null,
      unit_cost: ig.cp, ppc_at_count: ig.ppc || 1
    });
  }
  if (rows.length) supabase.from('inventory_counts').upsert(rows, { onConflict: 'org_id,ingredient_id,date' }).then(r => handleResult(r, 'inventory'));
}

// --- Ingredients ---
export function updateIngredient(id, fields) {
  const map = {};
  if ('n' in fields) map.name = typeof fields.n==='string'?fields.n.toUpperCase():fields.n;
  if ('b' in fields) map.brand = typeof fields.b==='string'?fields.b.toUpperCase():fields.b;
  if ('cat' in fields) map.category = fields.cat;
  if ('dist' in fields) map.distributor = typeof fields.dist==='string'?fields.dist.toUpperCase():fields.dist;
  if ('cq' in fields) map.case_qty = fields.cq;
  if ('cu' in fields) map.unit = fields.cu;
  if ('cp' in fields) map.case_price = fields.cp;
  if ('yp' in fields) map.yield_pct = fields.yp;
  if ('tier' in fields) map.tier = fields.tier;
  if ('loc' in fields) map.location = fields.loc;
  if ('par' in fields) map.par = fields.par;
  if ('so' in fields) map.sort_order = fields.so;
  if ('vin' in fields) map.vendor_item_number = typeof fields.vin==='string'?fields.vin.toUpperCase():fields.vin;
  if ('ppc' in fields) map.packs_per_case = fields.ppc;
  if ('spp' in fields) map.size_per_pack = fields.spp;
  // pack_size is a legacy column — no longer written
  if ('ec' in fields) map.expense_category = fields.ec;
  if ('pt' in fields) map.pack_type = fields.pt;
  if ('slc' in fields) map.slices_per_pack = fields.slc;
  if ('srv' in fields) map.servings_per_pack = fields.srv;
  if ('sru' in fields) map.servings_unit = fields.sru;
  if ('pdu' in fields) map.pricing_display_unit = fields.pdu;
  if ('pcu' in fields) map.preferred_cost_unit = fields.pcu;
  if ('categoryId' in fields) map.category_id = fields.categoryId;
  if ('categorySource' in fields) map.category_source = fields.categorySource;
  if ('categoryConfidence' in fields) map.category_confidence = fields.categoryConfidence;
  if (Object.keys(map).length) supabase.from('ingredients').update(map).eq('id', id).then(r => handleResult(r, 'ingredient'));
}
export function logPriceChange(orgId, userId, ingredientId, oldPrice, newPrice, source='manual') {
  if (Math.abs(oldPrice - newPrice) < 0.01) return;
  if (!orgId || !userId) { console.warn('logPriceChange: missing orgId or userId'); return; }
  supabase.from('price_history').insert({
    user_id: userId, org_id: orgId, ingredient_id: ingredientId,
    old_price: oldPrice, new_price: newPrice, source
  }).then(({error}) => { if (error) console.error('Price history insert error:', error); });
}
export async function insertIngredient(orgId, userId, ig) {
  if (!requireIds(orgId, userId, 'add ingredient')) return;
  const result = await supabase.from('ingredients').insert({
    id: ig.id, user_id: userId, org_id: orgId, name: ig.n, brand: ig.b, category: ig.cat,
    distributor: typeof ig.dist==='string'?ig.dist.toUpperCase():ig.dist, case_qty: ig.cq, unit: ig.cu, case_price: ig.cp,
    yield_pct: ig.yp || 100, tier: ig.tier || 3, location: ig.loc || 'Dry Storage',
    par: ig.par || 1, sort_order: ig.so || 0,
    vendor_item_number: ig.vin || null, packs_per_case: ig.ppc || 1, size_per_pack: ig.spp || ig.cq,
    expense_category: ig.ec || 'Food',
    pack_type: ig.pt || null, slices_per_pack: ig.slc || null, servings_per_pack: ig.srv || null, servings_unit: ig.sru || null, pricing_display_unit: ig.pdu || null, preferred_cost_unit: ig.pcu || null,
    ...(ig.lastPriceDate ? { last_price_date: ig.lastPriceDate } : {}),
    ...(ig.categoryId ? { category_id: ig.categoryId } : {}),
    ...(ig.categorySource ? { category_source: ig.categorySource } : {}),
    ...(ig.categoryConfidence != null ? { category_confidence: ig.categoryConfidence } : {})
  });
  handleResult(result, 'add ingredient');
  return result;
}
export function deleteIngredient(id) {
  supabase.from('ingredients').delete().eq('id', id).then(r => handleResult(r, 'delete ingredient'));
}
export function deactivateIngredient(id) {
  supabase.from('ingredients').update({is_active:false}).eq('id', id).then(r => handleResult(r, 'deactivate ingredient'));
}

// --- Menu Items ---
export function updateMenuItem(id, fields) {
  const map = {};
  if ('p' in fields) map.price = fields.p;
  if ('n' in fields) map.name = fields.n;
  if ('cat' in fields) map.category = fields.cat;
  if ('yield' in fields) map.yield = fields.yield;
  if (Object.keys(map).length) supabase.from('menu_items').update(map).eq('id', id).then(r => handleResult(r, 'menu item'));
  // Keep recipe in sync
  const rmap = {};
  if ('n' in fields) rmap.name = fields.n;
  if ('yield' in fields) rmap.yield = fields.yield;
  if (Object.keys(rmap).length) supabase.from('recipes').update(rmap).eq('id', 'r_' + id).then(r => handleResult(r, 'recipe'));
}
export async function insertMenuItem(orgId, userId, mi) {
  if (!requireIds(orgId, userId, 'add menu item')) return;
  const recipeId = 'r_' + mi.id;
  // Create recipe first (recipe_ingredients FK requires it)
  await supabase.from('recipes').insert({
    id: recipeId, user_id: userId, org_id: orgId, name: mi.n, yield: mi.yield || 1,
  });
  const r = await supabase.from('menu_items').insert({
    id: mi.id, user_id: userId, org_id: orgId, name: mi.n, category: mi.cat || mi.c, price: mi.p, yield: mi.yield || 1,
    recipe_id: recipeId,
    ...(mi.lastYield != null ? { last_yield: mi.lastYield } : {}),
    ...(mi.lastYieldUnit ? { last_yield_unit: mi.lastYieldUnit } : {})
  });
  handleResult(r, 'add menu item');
}
export async function deleteMenuItem(id) {
  // Check if the recipe is shared before deleting it
  const recipeId = 'r_' + id;
  await supabase.from('menu_items').delete().eq('id', id);
  // Only delete the recipe if no other items reference it
  const { count } = await supabase.from('menu_items').select('id', { count: 'exact', head: true }).eq('recipe_id', recipeId);
  if ((count || 0) === 0) {
    supabase.from('recipes').delete().eq('id', recipeId).then(r => handleResult(r, 'delete recipe'));
  }
}
export async function syncMenuItemIngredients(orgId, userId, menuItemId, lines, batchIds, recipeIdOverride) {
  if (!requireIds(orgId, userId, 'recipe lines')) return;
  let recipeId = recipeIdOverride;
  if (!recipeId) {
    const { data: miRow } = await supabase.from('menu_items').select('recipe_id,name,yield').eq('id', menuItemId).single();
    recipeId = miRow?.recipe_id;
  }
  if (!recipeId) recipeId = 'r_' + menuItemId;
  // Ensure the recipe row exists (handles first ingredient on uncosted items)
  const { data: existingRecipe } = await supabase.from('recipes').select('id').eq('id', recipeId).single();
  if (!existingRecipe) {
    const { data: miRow2 } = await supabase.from('menu_items').select('name,yield').eq('id', menuItemId).single();
    await supabase.from('recipes').insert({
      id: recipeId, user_id: userId, org_id: orgId,
      name: miRow2?.name || menuItemId, yield: miRow2?.yield || 1,
    });
    await supabase.from('menu_items').update({ recipe_id: recipeId }).eq('id', menuItemId);
  }

  // Write to recipe_ingredients (primary — reads come from here)
  await supabase.from('recipe_ingredients').delete().eq('recipe_id', recipeId);
  if (lines.length) {
    const recipeRows = lines.map((l, i) => {
      const isBatch = batchIds.has(l.i);
      return {
        user_id: userId, org_id: orgId, recipe_id: recipeId,
        ingredient_id: isBatch ? null : l.i, batch_id: isBatch ? l.i : null,
        qty: parseFloat(l.q)||0, unit: l.u, sort_order: i
      };
    });
    const r = await supabase.from('recipe_ingredients').insert(recipeRows);
    handleResult(r, 'recipe lines');
  }

  // Mirror to menu_item_ingredients for single-item recipes (rollback safety)
  // Skip mirror for shared recipes — menu_item_ingredients can't represent them
  const { count } = await supabase.from('menu_items').select('id', { count: 'exact', head: true }).eq('recipe_id', recipeId);
  if ((count || 0) <= 1) {
    await supabase.from('menu_item_ingredients').delete().eq('menu_item_id', menuItemId);
    if (lines.length) {
      const miiRows = lines.map((l, i) => {
        const isBatch = batchIds.has(l.i);
        return {
          user_id: userId, org_id: orgId, menu_item_id: menuItemId,
          ingredient_id: isBatch ? null : l.i, batch_id: isBatch ? l.i : null,
          qty: parseFloat(l.q)||0, unit: l.u, sort_order: i
        };
      });
      await supabase.from('menu_item_ingredients').insert(miiRows);
    }
  }
}

// Attach a menu item to an existing recipe
export async function attachToRecipe(menuItemId, recipeId) {
  const { error } = await supabase.from('menu_items').update({ recipe_id: recipeId }).eq('id', menuItemId);
  if (error) console.error('attachToRecipe error:', error);
  return { error };
}

// Detach a menu item from its shared recipe (set recipe_id to null)
export async function detachFromRecipe(menuItemId) {
  const { error } = await supabase.from('menu_items').update({ recipe_id: null }).eq('id', menuItemId);
  if (error) console.error('detachFromRecipe error:', error);
  return { error };
}

// --- Batches ---
export function updateBatch(id, fields) {
  const map = {};
  if ('n' in fields) map.name = fields.n;
  if ('yield' in fields) map.yield = fields.yield;
  if ('yu' in fields) map.yield_unit = fields.yu;
  if (Object.keys(map).length) supabase.from('batches').update(map).eq('id', id).then(r => handleResult(r, 'batch'));
}
export function insertBatch(orgId, userId, b) {
  if (!requireIds(orgId, userId, 'add batch')) return;
  supabase.from('batches').insert({
    id: b.id, user_id: userId, org_id: orgId, name: b.n, yield: b.yield, yield_unit: b.yu,
    ...(b.lastPrice != null ? { last_price: b.lastPrice } : {})
  }).then(r => handleResult(r, 'add batch'));
}
export function deleteBatch(id) {
  supabase.from('batches').delete().eq('id', id).then(r => handleResult(r, 'delete batch'));
}
export async function syncBatchIngredients(orgId, userId, batchId, lines, batchIds) {
  if (!requireIds(orgId, userId, 'batch ingredients')) return;
  await supabase.from('batch_ingredients').delete().eq('batch_id', batchId);
  if (!lines.length) return;
  const rows = lines.map((l, i) => {
    const isSub = batchIds && batchIds.has(l.i);
    return {
      user_id: userId, org_id: orgId, batch_id: batchId,
      ingredient_id: isSub ? null : l.i, sub_batch_id: isSub ? l.i : null,
      qty: parseFloat(l.q)||0, unit: l.u, sort_order: i
    };
  });
  const r = await supabase.from('batch_ingredients').insert(rows);
  handleResult(r, 'batch ingredients');
}

// --- Fixed Costs ---
export function upsertFixedCost(orgId, userId, fc) {
  if (!requireIds(orgId, userId, 'fixed cost')) return;
  supabase.from('fixed_costs').upsert({
    id: fc.id, user_id: userId, org_id: orgId, name: fc.n, amount: fc.amt, period: fc.per, category: fc.category || null
  }).then(r => handleResult(r, 'fixed cost'));
}
export function deleteFixedCost(id) {
  supabase.from('fixed_costs').delete().eq('id', id).then(r => handleResult(r, 'delete fixed cost'));
}

// --- Settings ---
// One settings row per org. Filter by org_id only (not user_id) so any authorized
// user can update regardless of who created the row. If no row exists yet, insert.
// Only writes the fields included in `fields` — never overwrites fields that didn't change.
export async function saveSettings(orgId, userId, fields) {
  if (!orgId || !userId) { console.warn('saveSettings: missing orgId or userId'); return; }
  if (!fields || !Object.keys(fields).length) return;
  const payload = {};
  if ('bizName' in fields) payload.biz_name = fields.bizName;
  if ('bizLoc' in fields) payload.biz_loc = fields.bizLoc;
  if ('weekStart' in fields) payload.week_start = fields.weekStart;
  if ('fcTarget' in fields) payload.fc_target = fields.fcTarget;
  if ('laborTarget' in fields) payload.labor_target = fields.laborTarget;
  if ('wasteTarget' in fields) payload.waste_target = fields.wasteTarget;
  if ('varTarget' in fields) payload.var_target = fields.varTarget;
  if ('rentPct' in fields) payload.rent_pct = fields.rentPct;
  if ('coffeePct' in fields) payload.coffee_pct = fields.coffeePct;
  if ('ccPct' in fields) payload.cc_processing_pct = fields.ccPct;
  if ('countDay' in fields) payload.count_day = fields.countDay;
  if ('wasteMode' in fields) payload.waste_mode = fields.wasteMode;
  if ('locations' in fields) payload.locations = fields.locations;
  if ('expenseCategories' in fields) payload.expense_categories = fields.expenseCategories;
  if ('salesCategories' in fields) payload.sales_categories = fields.salesCategories;
  if (!Object.keys(payload).length) return;
  const { data, error } = await supabase.from('settings').update(payload).eq('org_id', orgId).select();
  if (error) { handleResult({ error }, 'settings'); return; }
  if (!data || data.length === 0) {
    // No row existed — insert a new one with all fields
    const fullPayload = {
      biz_name: fields.bizName ?? '', biz_loc: fields.bizLoc ?? '', week_start: fields.weekStart ?? 0,
      fc_target: fields.fcTarget ?? 30, labor_target: fields.laborTarget ?? 28,
      waste_target: fields.wasteTarget ?? 2, var_target: fields.varTarget ?? 5,
      rent_pct: fields.rentPct ?? 0, coffee_pct: fields.coffeePct ?? 0, cc_processing_pct: fields.ccPct ?? null, count_day: fields.countDay ?? 0,
      waste_mode: fields.wasteMode ?? 'daily',
      locations: fields.locations ?? [], expense_categories: fields.expenseCategories ?? [],
      sales_categories: fields.salesCategories ?? null
    };
    const r = await supabase.from('settings').insert({ user_id: userId, org_id: orgId, ...fullPayload });
    handleResult(r, 'settings');
  } else {
    handleResult({ error: null }, 'settings');
  }
}

export async function upsertDeduction(orgId, ded) {
  const payload = { org_id: orgId, name: ded.name, type: ded.type, value: ded.value, category: ded.category || null, is_active: ded.is_active ?? true, sort_order: ded.sort_order ?? 0 };
  if (ded.id) payload.id = ded.id;
  const r = await supabase.from('custom_deductions').upsert(payload, { onConflict: 'id' }).select();
  handleResult(r, 'deduction');
  return r.data?.[0] || null;
}

export async function deleteDeduction(id) {
  const r = await supabase.from('custom_deductions').delete().eq('id', id);
  handleResult(r, 'deduction');
}
